/**
 * Canvas Data Source Types
 *
 * Defines the data sources available for canvas widgets.
 * Each source represents a specific piece of data that can be fetched
 * from the backend and displayed in a widget.
 *
 * Sources are grouped by domain (e.g., asset.*, reports.*) and map to
 * GraphQL queries via query groups.
 *
 * Data Source → Query Group Mapping:
 *
 * asset.breakdown (AssetBreakdownAt):
 *   totalUnits, annualRent, leasableArea, rentPerSqm, vacancyRateKpi,
 *   churnRolling12mKpi
 *
 * asset.businessPlan (PropertyBusinessPlan):
 *   businessPlanPrice, businessPlanPriceSqm, allocatedPurchasePrice, appCost
 *
 * asset.breakdownBetween (AssetBreakdownBetween):
 *   rentByTypeGroup, vacancyRate, vacancyRateByArea, idleTenancyRate,
 *   idleAnnualAmount, churnRate, rentDevelopmentYoy, wault, waultKpi
 *
 * asset.propertyTenancies (CanvasPropertyTenancies):
 *   unitTypeByArea, unitTypeByCount
 *
 * asset.breakdownsList (AssetBreakdownsAt):
 *   breakdownsList
 *
 * asset.yield (CanvasYieldQuery — AssetTotalReturn):
 *   totalReturnOverTime, totalReturnPercentOverTime,
 *   totalReturnPercentWaterfall, propertyValueKpi,
 *   propertyValueLastQuarterKpi, returnActualKpi
 *
 * asset.budgetedReturn (AssetBudgetReturn + category tree, via CanvasYieldQuery):
 *   returnBudgetedKpi
 *
 * asset.operatingMargin (CanvasOperatingMarginQuery):
 *   operatingMarginOverTime, netOperatingIncomeOverTime
 *
 * asset.irr (AssetIrr):
 *   irrKpi
 */

import moment from "moment";
import type { TranslationKey } from "~/helpers/common-helpers";

// ============================================================================
// DATA SOURCE ENUM
// ============================================================================

/**
 * Enum of all available data sources.
 * Each source key maps to a specific data point from the backend.
 *
 * Naming convention: ASSET_METRIC (e.g., ASSET_ANNUAL_RENT)
 * String value: asset.metric (e.g., "asset.annualRent")
 *
 * NOTE: Only sources with actual GraphQL query implementations are included.
 */
export enum DataSource {
  // Asset KPIs - from AssetBreakdownAt query
  ASSET_TOTAL_UNITS = "asset.totalUnits",
  ASSET_ANNUAL_RENT = "asset.annualRent",
  ASSET_LEASABLE_AREA = "asset.leasableArea",
  ASSET_RENT_PER_SQM = "asset.rentPerSqm",

  // Asset Business Plan - from PropertyBusinessPlan query
  ASSET_BUSINESS_PLAN_PRICE = "asset.businessPlanPrice",
  ASSET_BUSINESS_PLAN_PRICE_SQM = "asset.businessPlanPriceSqm",
  ASSET_ALLOCATED_PURCHASE_PRICE = "asset.allocatedPurchasePrice",
  ASSET_APP_COST = "asset.appCost",

  /**
   * Asset Time Series Charts - from AssetBreakdownBetween query
   *
   * These data sources provide time series data with:
   * - Breakdown by typeGroup (Residential, Commercial, etc.)
   * - Date range filtering via dateRange filter widget
   * - Time aggregation (monthly, quarterly, yearly)
   * - Idle/churn/vacancy metrics derived from rent components
   */
  ASSET_RENT_BY_TYPE_GROUP = "asset.rentByTypeGroup",
  ASSET_VACANCY_RATE = "asset.vacancyRate",
  // Area-weighted vacancy rate - gives a more accurate exposure picture when unit sizes vary
  ASSET_VACANCY_RATE_BY_AREA = "asset.vacancyRateByArea",
  ASSET_IDLE_TENANCY_RATE = "asset.idleTenancyRate",
  ASSET_IDLE_ANNUAL_AMOUNT = "asset.idleAnnualAmount",
  ASSET_RENT_PER_SQM_OVER_TIME = "asset.rentPerSqmOverTime",

  // Asset Churn Rate - from AssetBreakdownBetween query
  ASSET_CHURN_RATE = "asset.churnRate",

  // Asset Terminations (opsigelser) over time - count of termination notices per period
  ASSET_TERMINATIONS_OVER_TIME = "asset.terminationsOverTime",

  // Asset Charts - from CanvasPropertyTenancies query (assetManagementProperty -> assetManagementTenancies)
  ASSET_UNIT_TYPE_BY_AREA = "asset.unitTypeByArea",
  ASSET_UNIT_TYPE_BY_COUNT = "asset.unitTypeByCount",

  // Asset Breakdowns List - from AssetBreakdownsAt query
  // Used for tables showing multiple assets (properties or companies)
  ASSET_BREAKDOWNS_LIST = "asset.breakdownsList",

  /**
   * Asset Total Return Over Time (Totalafkast over tid) - from CanvasYieldQuery
   *
   * Waterfall chart showing direct and indirect yield grouped by years.
   * Used for visualizing cumulative returns over time with breakdown by yield type.
   */
  ASSET_TOTAL_RETURN_OVER_TIME = "asset.totalReturnOverTime",

  /**
   * Asset Total Return % Over Time - from CanvasYieldQuery
   *
   * Bar chart showing total return as a percentage period-over-period.
   * Direct yield % = NOI / beginning-of-period property value × 100.
   * Indirect yield % = value appreciation / beginning-of-period property value × 100.
   * Better suited for comparing returns between consecutive periods than the waterfall.
   */
  ASSET_TOTAL_RETURN_PERCENT_OVER_TIME = "asset.totalReturnPercentOverTime",

  /**
   * Asset Total Return % Waterfall - from CanvasYieldQuery
   *
   * Waterfall chart showing cumulative total return as a percentage from a
   * static start-date baseline.  All period yields are expressed relative to
   * the property value at the chosen baseline date so the bars stack to the
   * cumulative return since inception.
   *   Direct yield %   = NOI[i] / PV_baseline × 100
   *   Indirect yield % = (PV[i] − PV[i-1]) / PV_baseline × 100
   */
  ASSET_TOTAL_RETURN_PERCENT_WATERFALL = "asset.totalReturnPercentWaterfall",

  /**
   * Asset Property Value KPI (Ejendomsværdi) — from CanvasYieldQuery
   *
   * Latest available quarterly snapshot of the property value
   * (rawBalancesByCategoryType with categoryType "property_value").
   * Use when the question is "what is the current / most recent valuation?".
   */
  ASSET_PROPERTY_VALUE_KPI = "asset.propertyValueKpi",

  /**
   * Asset Property Value KPI — last concluded quarter — from CanvasYieldQuery
   *
   * Property value as of the last fully completed quarter (the quarter preceding today).
   * Use when the business requirement is to show a confirmed, closed-quarter valuation.
   */
  ASSET_PROPERTY_VALUE_LAST_QUARTER_KPI = "asset.propertyValueLastQuarterKpi",

  /**
   * Asset Operating Margin Over Time - from CanvasOperatingMarginQuery
   *
   * Bar chart showing operating margin (NOI / Revenue) grouped by years.
   * Used for visualizing operating efficiency over time.
   */
  ASSET_OPERATING_MARGIN_OVER_TIME = "asset.operatingMarginOverTime",

  /**
   * Asset NOI Over Time - from CanvasOperatingMarginQuery
   *
   * Bar chart showing absolute Net Operating Income (NOI) grouped by years.
   * Uses the same underlying data as ASSET_OPERATING_MARGIN_OVER_TIME but returns
   * the raw NOI amount instead of a percentage.
   */
  ASSET_NET_OPERATING_INCOME_OVER_TIME = "asset.netOperatingIncomeOverTime",

  /**
   * Asset Rent Development YoY - derived from AssetBreakdownBetween query
   *
   * Bar chart showing rental income year-over-year as percentage change.
   * Uses the same breakdownBetween data source but extends the date range
   * by one year to compute YoY comparison.
   */
  ASSET_RENT_DEVELOPMENT_YOY = "asset.rentDevelopmentYoy",

  /**
   * Asset WAULT Over Time - from AssetBreakdownBetween query
   *
   * Bar chart showing Weighted Average Unexpired Lease Term (WAULT) over time.
   * Uses the wault field from AssetBreakdownPeriod, broken down by typeGroup.
   */
  ASSET_WAULT = "asset.wault",

  /**
   * Asset WAULT KPI - latest WAULT value as a single metric
   *
   * Extracts the most recent WAULT value from the AssetBreakdownBetween query
   * for use in key-value widgets.
   */
  ASSET_WAULT_KPI = "asset.waultKpi",

  /**
   * Asset Annual Churn (Rolling 12M) KPI - single metric from AssetBreakdownBetween query
   *
   * Extracts the most recent churnRolling12MRate value (rolling 12-month basis)
   * for use in key-value widgets. A higher value indicates more tenant turnover.
   */
  ASSET_CHURN_ROLLING_12M_KPI = "asset.churnRolling12mKpi",

  /**
   * Asset Vacancy Rate KPI - single vacancy rate metric from AssetBreakdownAt query
   *
   * Calculates physical vacancy rate (vacantCount / totalCount * 100) from the
   * AssetBreakdownAt summary for use in key-value widgets.
   */
  ASSET_VACANCY_RATE_KPI = "asset.vacancyRateKpi",

  /**
   * Asset Economic Vacancy Rate KPI - economic vacancy from AssetBreakdownAt query
   *
   * Calculates economic vacancy rate (rents[0].idleRate * 100) — the share of
   * potential rent lost to vacancy — for use in key-value widgets.
   */
  ASSET_ECONOMIC_VACANCY_RATE_KPI = "asset.economicVacancyRateKpi",

  /**
   * Asset Area Vacancy Rate KPI - area-based vacancy from AssetBreakdownAt query
   *
   * Calculates area vacancy rate (areas[0].idleRate * 100) — the share of
   * leasable area currently vacant — for use in key-value widgets.
   */
  ASSET_AREA_VACANCY_RATE_KPI = "asset.areaVacancyRateKpi",

  /**
   * Asset Return KPI — actual total return for a widget-defined period.
   *
   * Total return (nominal amount) = NOI + property-value change.
   * Period is driven by the widget's own `variables.dateRange` (from/to),
   * NOT the canvas-level dateRange filter. This prevents the chart's date
   * range from leaking into the KPI.
   * Falls back to the last completed calendar year when no dateRange is set.
   * Derived from the same CanvasYieldQuery data used for TOTAL_RETURN_PERCENT_OVER_TIME.
   */
  ASSET_RETURN_ACTUAL_KPI = "asset.returnActualKpi",

  /**
   * Asset Return KPI — budgeted return for a widget-defined period.
   *
   * Budgeted NOI (nominal amount) for the period, from the default budget.
   * Period is driven by the widget's own `variables.dateRange` (from/to),
   * NOT the canvas-level dateRange filter.
   * Falls back to the current calendar year when no dateRange is set.
   * Derived from the property's default budget records mapped to the NOI category tree.
   * Requires the "asset.budgetedReturn" query group to be enabled.
   */
  ASSET_RETURN_BUDGETED_KPI = "asset.returnBudgetedKpi",

  /**
   * Asset Direct Yield KPI — NOI / beginning-of-period property value × 100.
   *
   * Period is driven by the widget's own `variables.dateRange` (from/to).
   * Falls back to the last completed calendar year when no dateRange is set.
   * Derived from the same CanvasYieldQuery data used for TOTAL_RETURN_PERCENT_OVER_TIME.
   */
  ASSET_DIRECT_YIELD_KPI = "asset.directYieldKpi",

  /**
   * Asset Indirect Yield KPI — property value change / beginning-of-period PV × 100.
   *
   * Period is driven by the widget's own `variables.dateRange` (from/to).
   * Falls back to the last completed calendar year when no dateRange is set.
   * Derived from the same CanvasYieldQuery data used for TOTAL_RETURN_PERCENT_OVER_TIME.
   */
  ASSET_INDIRECT_YIELD_KPI = "asset.indirectYieldKpi",

  /**
   * Asset Historical IRR KPI — Internal Rate of Return.
   *
   * Computes the IRR from the property's acquisition date to the widget-configured
   * end date (or today). Uses property value snapshots as GAV₀/GAVₙ and income
   * statement cash flows from a configurable categoryType (e.g. "noi").
   * The categoryType defaults to "noi" but can be overridden via widget
   * variables.categoryType for customer-specific setups.
   * Requires the "asset.irr" query group to be enabled.
   */
  ASSET_IRR_KPI = "asset.irrKpi",

  /** Top tenancies by yearly rent — table with name, type, area, rent, status */
  ASSET_TOP_TENANCIES_BY_RENT = "asset.topTenanciesByRent",

  /** Rented (occupied) units count from AssetBreakdownAt */
  ASSET_RENTED_UNITS_KPI = "asset.rentedUnitsKpi",
  /** Vacant units count from AssetBreakdownAt */
  ASSET_VACANT_UNITS_KPI = "asset.vacantUnitsKpi",
  /** Rented (occupied) area in m² from AssetBreakdownAt */
  ASSET_RENTED_AREA_KPI = "asset.rentedAreaKpi",
  /** Vacant area in m² from AssetBreakdownAt */
  ASSET_VACANT_AREA_KPI = "asset.vacantAreaKpi",
  /** Vacancy amount in DKK (BASE_RENT idleAnnualAmount) from AssetBreakdownAt */
  ASSET_VACANCY_AMOUNT_KPI = "asset.vacancyAmountKpi",

  /** Rental income (GRI) KPI — latest period value from income statements */
  ASSET_RENTAL_INCOME_KPI = "asset.rentalIncomeKpi",
  /** NOI margin KPI — NOI / GRI × 100 for the latest period */
  ASSET_NOI_MARGIN_KPI = "asset.noiMarginKpi",
  /** Results margin KPI — full result / GRI × 100 for the latest period */
  ASSET_RESULTS_MARGIN_KPI = "asset.resultsMarginKpi",
  /** Combined nominal time series: rental income, NOI, and full result */
  ASSET_INCOME_NOMINAL_OVER_TIME = "asset.incomeNominalOverTime",

  /**
   * Income by category — table with one row per income category (GRI / NOI / Result)
   * and columns realized, budget, realizedPrev for the configured date range.
   * Range is driven by the canvas dateRange filter (or per-widget variables.dateRange).
   * Defaults preserve YTD behaviour: to = end of last fully completed month, from = Jan 1 of that year.
   */
  ASSET_INCOME_BY_CATEGORY = "asset.incomeByCategory",

  /**
   * Rent Growth (Rolling 12M) KPI — percentage change in BASE_RENT between
   * the latest monthly period and the period 12 months earlier.
   */
  ASSET_RENT_GROWTH_ROLLING_12M_KPI = "asset.rentGrowthRolling12mKpi",

  /** Tenant rent distribution — pie/donut showing top 10 tenants + "Øvrige" by share of total annual contract rent */
  ASSET_TENANT_RENT_DISTRIBUTION = "asset.tenantRentDistribution",

  /**
   * Arrears (restancer) KPIs — property-level aging from a single `assetAging`
   * call. All use the `asset.assetAging` query group. (The per-lease table below
   * still uses the `asset.arrears` leaseAging fan-out.)
   */
  ASSET_ARREARS_TOTAL_KPI = "asset.arrearsTotalKpi",
  ASSET_ARREARS_CURRENT_KPI = "asset.arrearsCurrentKpi",
  /** Sum of overdue buckets (0-30 + 31-60 + 61-90 + 90+), excluding the not-yet-due "current" bucket. */
  ASSET_ARREARS_DUE_TOTAL_KPI = "asset.arrearsDueTotalKpi",
  ASSET_ARREARS_DUE_0_30_KPI = "asset.arrearsDue0To30Kpi",
  ASSET_ARREARS_DUE_31_60_KPI = "asset.arrearsDue31To60Kpi",
  ASSET_ARREARS_DUE_61_90_KPI = "asset.arrearsDue61To90Kpi",
  ASSET_ARREARS_DUE_90_PLUS_KPI = "asset.arrearsDue90PlusKpi",

  /** Per-lease arrears table: tenant, typeGroup, evictionStatus, amount. */
  ASSET_ARREARS_BY_LEASE = "asset.arrearsByLease",

  /**
   * Rent-at-lease-start scatter — one point per lease: x = lease start date,
   * y = gross annual rent at that start, series = typeGroup. Backed by the
   * `asset.rentAtLeaseStart` group (tenancies list + leaseRent fan-out).
   */
  ASSET_RENT_AT_LEASE_START_SCATTER = "asset.rentAtLeaseStartScatter",

  /**
   * Vacant tenancies table — one row per tenancy whose current tenant is a
   * ghost (backend's placeholder for vacant). Columns: identifier, typeGroup,
   * area, annualRent (potential), status. Shares data with topTenancies.
   */
  ASSET_VACANT_TENANCIES_TABLE = "asset.vacantTenanciesTable",

  /**
   * Break options table — tenancies whose next break opportunity (earliest of
   * tenant's irrevocableUntilLandlord / irrevocableUntilTenant) falls within
   * a configurable horizon (default 12 months). Sorted by next-break date asc.
   */
  ASSET_BREAK_OPTIONS_TABLE = "asset.breakOptionsTable",

  /**
   * Area vs economic vacancy rate over time — single chart with two series.
   * Area % = idleArea / totalArea. Economic % = idle BASE_RENT / (contract + idle) BASE_RENT.
   */
  ASSET_VACANCY_RATE_AREA_VS_ECONOMIC = "asset.vacancyRateAreaVsEconomic",

  /**
   * New lettings (tenancies whose current non-ghost tenant moved in within the
   * configurable horizon, default 12 months) distributed by typeGroup. Donut/pie chart.
   */
  ASSET_NEW_LETTINGS_BY_TYPE_GROUP = "asset.newLettingsByTypeGroup",

  /**
   * Property master data — name, Adm ID (externalID of type "id"), and total units.
   * Displayed in the property master data widget replacing the total units KPI card.
   */
  ASSET_MASTER_DATA = "asset.masterData",
}

// ============================================================================
// TIME RANGE
// ============================================================================

/** Time range granularity for grouping time series data */
export type TimeRange = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";

/**
 * Get the moment.js group key format for a given time range.
 * Used to bucket snapshots by period when aggregating time series.
 */
export const getTimeRangeGroupKey = (timeRange: TimeRange, date: moment.Moment): string => {
  switch (timeRange) {
    case "daily":
      return date.format("YYYY-MM-DD");
    case "weekly":
      return `${date.isoWeekYear()}-W${String(date.isoWeek()).padStart(2, "0")}`;
    case "monthly":
      return date.format("YYYY-MM");
    case "quarterly":
      return `${date.year()}-Q${date.quarter()}`;
    case "yearly":
      return date.format("YYYY");
  }
};

/**
 * Format a period group key for display on chart axes.
 */
export const formatTimeRangeLabel = (timeRange: TimeRange, date: moment.Moment): string => {
  switch (timeRange) {
    case "daily":
      return date.format("MMM D, YYYY");
    case "weekly":
      return `W${date.isoWeek()} ${date.format("YYYY")}`;
    case "monthly":
      return date.format("MMM YYYY");
    case "quarterly":
      return `Q${date.quarter()} ${date.format("YYYY")}`;
    case "yearly":
      return date.format("YYYY");
  }
};

// ============================================================================
// DATA TYPES
// ============================================================================

/**
 * Key-value widget data (single metric with optional change indicator)
 */
export interface KeyValueData {
  value: number | string;
  change?: number; // Percentage change from previous period
  share?: number; // Optional share of the unfiltered total (0..1), rendered beside the value as "(60%)"
  currency?: string; // Currency code (e.g., "DKK", "EUR") for formatting
  label?: string; // Optional descriptive label (e.g., budget name for budgeted KPIs)
}

/**
 * Simple chart data point (single series)
 */
export interface ChartDataPoint {
  label: string;
  value: number;
}

/**
 * Multi-series chart data point
 */
export interface MultiSeriesDataPoint {
  category: string;
  series: Array<{ name: string; value: number }>;
}

/**
 * Sunburst chart node (hierarchical, for ECharts sunburst)
 */
export interface SunburstNode {
  name: string;
  value?: number;
  children?: SunburstNode[];
}

/**
 * Sunburst chart data - two-level hierarchy:
 * outer ring = typeGroup (RESIDENTIAL, COMMERCIAL, OTHER)
 * inner slices = tenancyType within each group
 */
export interface SunburstChartData {
  /** Metric being shown: "area" | "count" */
  metric: "area" | "count";
  children: SunburstNode[];
}

/**
 * Time series chart data (for ApexCharts)
 * Contains categories (x-axis labels) and series with values per category
 */
export interface TimeSeriesChartData {
  categories: string[];
  series: Array<{
    name: string;
    type?: "line" | "bar" | "area";
    data: number[];
    /** Which Y-axis this series belongs to (0 = left, 1 = right). Default 0. */
    yAxisIndex?: number;
    /** Override line style for this series (used for accumulated / dashed lines). */
    lineStyle?: { type?: "solid" | "dashed" | "dotted"; width?: number };
    /** Suppress symbol dots on line series. Default: show symbols */
    showSymbol?: boolean;
    /**
     * Force a specific index into the resolved color palette, overriding the default
     * sequential assignment. Used to pair accumulated lines with their bar counterparts.
     */
    colorIndex?: number;
  }>;
  /**
   * Per-category metadata (parallel to `categories` array).
   * Populated by providers that need to pass click context (e.g. assetIds for drill-down).
   * Accessed via `meta[dataIndex]` in ON_DATA_CLICK handlers.
   */
  meta?: unknown[];
}

/**
 * Scatter chart data — points on an (x, y) plane (no shared category axis).
 * Used for e.g. rent-at-lease-start: x = lease start time, y = annual rent.
 */
export interface ScatterDataPoint {
  /** X value. ISO date string for a time axis, or a number for a value axis. */
  x: string | number;
  /** Y value (numeric). */
  y: number;
  /** Optional series key — points sharing a series render in one colour (e.g. typeGroup). */
  series?: string;
  /** Optional click/tooltip metadata (e.g. leaseId, tenancy name). */
  meta?: Record<string, unknown>;
}

export interface ScatterData {
  /** All points across all series. */
  points: ScatterDataPoint[];
  /** X axis type — "time" for ISO date strings, "value" for numbers. Default "time". */
  xAxisType?: "time" | "value";
  /** Optional axis labels. */
  xAxisLabel?: string;
  yAxisLabel?: string;
  /** Currency for y values (formatting / tooltip). */
  currency?: string;
}

/**
 * Table row data (generic record)
 */
export type TableRowData = Record<string, unknown>;

/**
 * Table data with pagination info (for canvas table widgets)
 */
export interface TableData {
  columns: Array<{ key: string; label: string }>;
  rows: TableRowData[];
  pagination?: {
    page: number;
    size: number;
    total: number;
    lastPage: number;
  };
}

/**
 * Key-value list widget data.
 * Flat list of labelled values — no separate title; include a name row in items if needed.
 */
export interface KeyValueListItem {
  /** Human-readable (already-translated) label */
  label: string;
  /** Formatted string value to display */
  value: string | null;
}

export interface KeyValueListData {
  /** Ordered list of label → value rows */
  items: KeyValueListItem[];
}

/**
 * Rich text widget data (HTML content from a data source)
 */
export interface TextData {
  /** HTML content to render in the TipTap editor (read-only) */
  html: string;
}

/**
 * Union type for all possible data source return types
 */
export type SourceData = KeyValueData | ChartDataPoint[] | MultiSeriesDataPoint[] | TimeSeriesChartData | SunburstChartData | TableRowData[] | TextData;

// ============================================================================
// SOURCE METADATA
// ============================================================================

/**
 * Metadata about a data source, including its type and which query it belongs to.
 * This helps the registry know how to fetch and type the data.
 */
export interface SourceMetadata {
  /**
   * The data source key (same string as the map entry key).
   * Typed as string rather than DataSource so provider-registered
   * org-specific sources (e.g. "financial.incomeOverview") can also
   * use this interface.
   */
  source: string;
  /** The type of data this source returns */
  dataType: "key-value" | "chart-simple" | "chart-multi" | "chart-timeseries" | "chart-scatter" | "chart-sunburst" | "table" | "text";
  /** The query group this source belongs to (for batching) */
  queryGroup: string;
  /** Human-readable description (developer-facing, used in docs/reference) */
  description?: string;
  /**
   * Translation key for the user-facing tooltip shown on the info icon in every widget
   * that uses this data source. The resolved text should explain — in plain language —
   * what the metric measures, what is included/excluded, and any key assumptions.
   * Leave blank to show no info icon for this source.
   */
  tooltipKey?: TranslationKey;
}

/**
 * Static metadata map for asset.* sources.
 *
 * Used ONLY for compile-time type utilities (DataSourcesByType<T>,
 * KeyValueDataSource, etc.). Runtime registration is done by
 * helpers/canvas/sources/asset-sources.ts via registerSourceMetadata().
 *
 * Do not use this for runtime lookups — use lookupSourceMetadata() instead.
 */
export const SOURCE_METADATA: Record<DataSource, SourceMetadata> = {
  // Asset KPIs - from AssetBreakdownAt query
  // Accepts optional typeGroups filter: when provided, aggregates segments[].period data
  // for the matching type groups instead of using the top-level summary.
  [DataSource.ASSET_TOTAL_UNITS]: {
    source: DataSource.ASSET_TOTAL_UNITS,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Total number of units in the asset. Accepts optional typeGroups filter to aggregate matching segments.",
  },
  [DataSource.ASSET_ANNUAL_RENT]: {
    source: DataSource.ASSET_ANNUAL_RENT,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Total annual rent for the asset. Accepts optional typeGroups filter to aggregate matching segments.",
  },
  [DataSource.ASSET_LEASABLE_AREA]: {
    source: DataSource.ASSET_LEASABLE_AREA,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Total leasable area in square meters. Accepts optional typeGroups filter to aggregate matching segments.",
  },
  [DataSource.ASSET_RENT_PER_SQM]: {
    source: DataSource.ASSET_RENT_PER_SQM,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Annual rent per square meter. Accepts optional typeGroups filter to aggregate matching segments.",
  },

  // Asset Business Plan - from PropertyBusinessPlan query
  [DataSource.ASSET_BUSINESS_PLAN_PRICE]: {
    source: DataSource.ASSET_BUSINESS_PLAN_PRICE,
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "Business plan price",
  },
  [DataSource.ASSET_BUSINESS_PLAN_PRICE_SQM]: {
    source: DataSource.ASSET_BUSINESS_PLAN_PRICE_SQM,
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "Business plan price per square meter",
  },
  [DataSource.ASSET_ALLOCATED_PURCHASE_PRICE]: {
    source: DataSource.ASSET_ALLOCATED_PURCHASE_PRICE,
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "Allocated purchase price",
  },
  [DataSource.ASSET_APP_COST]: {
    source: DataSource.ASSET_APP_COST,
    dataType: "key-value",
    queryGroup: "asset.businessPlan",
    description: "APP plus additional costs",
  },

  /**
   * Asset Time Series Charts - from AssetBreakdownBetween query
   *
   * All time series data comes from AssetBreakdownBetween which provides:
   * - Rent development broken down by typeGroup
   * - Idle/churn rent amounts (lost rent due to vacancy)
   * - Vacancy rates (economic idle rate)
   * - Supports date range filtering and time aggregation
   */
  [DataSource.ASSET_RENT_BY_TYPE_GROUP]: {
    source: DataSource.ASSET_RENT_BY_TYPE_GROUP,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Rent development by typeGroup (from AssetBreakdownBetween). Shows Total + breakdown by Residential, Commercial, etc.",
  },
  [DataSource.ASSET_VACANCY_RATE]: {
    source: DataSource.ASSET_VACANCY_RATE,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Vacancy rate over time by typeGroup (from AssetBreakdownBetween). Calculated as vacantCount/totalCount.",
  },
  [DataSource.ASSET_VACANCY_RATE_BY_AREA]: {
    source: DataSource.ASSET_VACANCY_RATE_BY_AREA,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description:
      "Vacancy rate over time weighted by area (from AssetBreakdownBetween). Uses areas[0].idleRate from the API directly — more accurate than the per-unit rate when unit sizes vary significantly.",
  },
  [DataSource.ASSET_IDLE_TENANCY_RATE]: {
    source: DataSource.ASSET_IDLE_TENANCY_RATE,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Idle tenancy rate (economic) by typeGroup (from AssetBreakdownBetween). Shows % of potential rent lost to vacancy.",
  },
  [DataSource.ASSET_IDLE_ANNUAL_AMOUNT]: {
    source: DataSource.ASSET_IDLE_ANNUAL_AMOUNT,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Idle annual rent amount (churn) by typeGroup (from AssetBreakdownBetween). Shows absolute DKK lost to vacancy.",
  },
  [DataSource.ASSET_RENT_PER_SQM_OVER_TIME]: {
    source: DataSource.ASSET_RENT_PER_SQM_OVER_TIME,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Annual rent per square meter over time by typeGroup (from AssetBreakdownBetween). Calculated as BASE_RENT / leasable area per period.",
  },

  // Asset Churn Rate - from AssetBreakdownBetween query
  [DataSource.ASSET_CHURN_RATE]: {
    source: DataSource.ASSET_CHURN_RATE,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Tenant churn rate over time by typeGroup (from AssetBreakdownBetween).",
  },
  [DataSource.ASSET_TERMINATIONS_OVER_TIME]: {
    source: DataSource.ASSET_TERMINATIONS_OVER_TIME,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Terminations (opsigelser) over time — count of termination notices received per period by typeGroup (from AssetBreakdownBetween).",
  },

  // Asset Charts - from CanvasPropertyTenancies query (assetManagementProperty -> assetManagementTenancies)

  [DataSource.ASSET_UNIT_TYPE_BY_AREA]: {
    source: DataSource.ASSET_UNIT_TYPE_BY_AREA,
    dataType: "chart-sunburst",
    queryGroup: "asset.propertyTenancies",
    description: "Unit type distribution by area — two-level sunburst: typeGroup > tenancyType (from assetManagementTenancies)",
  },
  [DataSource.ASSET_UNIT_TYPE_BY_COUNT]: {
    source: DataSource.ASSET_UNIT_TYPE_BY_COUNT,
    dataType: "chart-sunburst",
    queryGroup: "asset.propertyTenancies",
    description: "Unit type distribution by count — two-level sunburst: typeGroup > tenancyType (from assetManagementTenancies)",
  },

  // Asset Breakdowns List - from AssetBreakdownsAt query
  [DataSource.ASSET_BREAKDOWNS_LIST]: {
    source: DataSource.ASSET_BREAKDOWNS_LIST,
    dataType: "table",
    queryGroup: "asset.breakdownsList",
    description: "List of asset breakdowns (properties or companies) with KPIs for table display",
  },

  // Asset Total Return (Yield) - from CanvasYieldQuery
  [DataSource.ASSET_TOTAL_RETURN_OVER_TIME]: {
    source: DataSource.ASSET_TOTAL_RETURN_OVER_TIME,
    dataType: "chart-timeseries",
    queryGroup: "asset.yield",
    description: "Total return over time (Totalafkast over tid). Waterfall chart with direct and indirect yield grouped by years.",
  },

  // Asset Total Return % Over Time - from CanvasYieldQuery (period-over-period % bar chart)
  [DataSource.ASSET_TOTAL_RETURN_PERCENT_OVER_TIME]: {
    source: DataSource.ASSET_TOTAL_RETURN_PERCENT_OVER_TIME,
    dataType: "chart-timeseries",
    queryGroup: "asset.yield",
    description:
      "Total return % period-over-period. Bar chart showing direct yield % (NOI / prev property value) and indirect yield % (value appreciation / prev property value) for each period.",
  },

  // Asset Total Return % Waterfall - from CanvasYieldQuery (cumulative % from static baseline)
  [DataSource.ASSET_TOTAL_RETURN_PERCENT_WATERFALL]: {
    source: DataSource.ASSET_TOTAL_RETURN_PERCENT_WATERFALL,
    dataType: "chart-timeseries",
    queryGroup: "asset.yield",
    description:
      "Total return % waterfall from a static baseline date. Direct yield % = NOI / baseline PV, Indirect yield % = PV change / baseline PV. Bars stack to show cumulative return since the baseline.",
  },

  // Asset Property Value KPI (latest quarterly snapshot) - from CanvasYieldQuery
  [DataSource.ASSET_PROPERTY_VALUE_KPI]: {
    source: DataSource.ASSET_PROPERTY_VALUE_KPI,
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Property value (ejendomsværdi) — latest available quarterly snapshot from rawBalancesByCategoryType(property_value). Reflects the most recent valuation in the data.",
  },

  // Asset Property Value KPI (last concluded quarter) - from CanvasYieldQuery
  [DataSource.ASSET_PROPERTY_VALUE_LAST_QUARTER_KPI]: {
    source: DataSource.ASSET_PROPERTY_VALUE_LAST_QUARTER_KPI,
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Property value (ejendomsværdi) — value at the last concluded quarter (the full quarter that ended before today). Use for confirmed, closed-quarter valuations.",
  },

  // Asset Return KPI — actual (period-driven via widget variables.dateRange)
  [DataSource.ASSET_RETURN_ACTUAL_KPI]: {
    source: DataSource.ASSET_RETURN_ACTUAL_KPI,
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Actual total return (NOI + property value change) as a nominal amount for the widget-configured period. Defaults to last completed calendar year. Period is set via widget variables.dateRange.",
  },

  // Asset Return KPI — budgeted (period-driven, requires asset.budgetedReturn query group)
  [DataSource.ASSET_RETURN_BUDGETED_KPI]: {
    source: DataSource.ASSET_RETURN_BUDGETED_KPI,
    dataType: "key-value",
    queryGroup: "asset.budgetedReturn",
    description:
      "Budgeted return (NOI from default budget) as a nominal amount for the widget-configured period. Defaults to current calendar year. Period is set via widget variables.dateRange.",
  },

  // Asset Direct Yield KPI — NOI / beginning-of-period PV (period-driven via widget variables.dateRange)
  [DataSource.ASSET_DIRECT_YIELD_KPI]: {
    source: DataSource.ASSET_DIRECT_YIELD_KPI,
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Direct yield % — NOI / beginning-of-period property value × 100. Period is set via widget variables.dateRange; defaults to last completed calendar year.",
  },

  // Asset Indirect Yield KPI — PV change / beginning-of-period PV (period-driven via widget variables.dateRange)
  [DataSource.ASSET_INDIRECT_YIELD_KPI]: {
    source: DataSource.ASSET_INDIRECT_YIELD_KPI,
    dataType: "key-value",
    queryGroup: "asset.yield",
    description:
      "Indirect yield % — property value change / beginning-of-period property value × 100. Period is set via widget variables.dateRange; defaults to last completed calendar year.",
  },

  // Asset Operating Margin - from CanvasOperatingMarginQuery
  [DataSource.ASSET_OPERATING_MARGIN_OVER_TIME]: {
    source: DataSource.ASSET_OPERATING_MARGIN_OVER_TIME,
    dataType: "chart-timeseries",
    queryGroup: "asset.operatingMargin",
    description: "Operating margin over time. Bar chart showing NOI/Revenue percentage grouped by years.",
  },

  // Asset Net Operating Income Over Time - from CanvasOperatingMarginQuery (absolute amount)
  [DataSource.ASSET_NET_OPERATING_INCOME_OVER_TIME]: {
    source: DataSource.ASSET_NET_OPERATING_INCOME_OVER_TIME,
    dataType: "chart-timeseries",
    queryGroup: "asset.operatingMargin",
    description: "NOI over time as an absolute amount. Bar chart grouped by years. Uses same data as operating margin.",
  },

  // Asset Rent Development YoY - derived from AssetBreakdownBetween query
  [DataSource.ASSET_RENT_DEVELOPMENT_YOY]: {
    source: DataSource.ASSET_RENT_DEVELOPMENT_YOY,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Rental income year-over-year percentage change. Derived from breakdownBetween data with extended date range.",
  },
  // Asset WAULT - from AssetBreakdownBetween query
  [DataSource.ASSET_WAULT]: {
    source: DataSource.ASSET_WAULT,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description: "Weighted Average Unexpired Lease Term (WAULT) over time by typeGroup (from AssetBreakdownBetween).",
  },

  // Asset WAULT KPI - latest value from AssetBreakdownBetween query
  [DataSource.ASSET_WAULT_KPI]: {
    source: DataSource.ASSET_WAULT_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdownBetween",
    description: "Latest WAULT value as a single KPI metric (from AssetBreakdownBetween).",
  },

  // Asset Vacancy Rate KPI - from AssetBreakdownAt query
  [DataSource.ASSET_VACANCY_RATE_KPI]: {
    source: DataSource.ASSET_VACANCY_RATE_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Physical vacancy rate as a single KPI (vacantCount / totalCount * 100) from AssetBreakdownAt. Accepts optional typeGroups filter to aggregate matching segments.",
  },

  // Asset Economic Vacancy Rate KPI - from AssetBreakdownAt query
  [DataSource.ASSET_ECONOMIC_VACANCY_RATE_KPI]: {
    source: DataSource.ASSET_ECONOMIC_VACANCY_RATE_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Economic vacancy rate as a single KPI (rents[0].idleRate * 100) from AssetBreakdownAt. Shows the share of potential rent lost to vacancy. Accepts optional typeGroups filter to aggregate matching segments.",
  },

  // Asset Area Vacancy Rate KPI - from AssetBreakdownAt query
  [DataSource.ASSET_AREA_VACANCY_RATE_KPI]: {
    source: DataSource.ASSET_AREA_VACANCY_RATE_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Area-based vacancy rate as a single KPI (areas[0].idleRate * 100) from AssetBreakdownAt. Shows the share of leasable area currently vacant. Accepts optional typeGroups filter to aggregate matching segments.",
  },

  // Asset Annual Churn (Rolling 12M) KPI - from AssetBreakdownAt point-in-time query
  [DataSource.ASSET_CHURN_ROLLING_12M_KPI]: {
    source: DataSource.ASSET_CHURN_ROLLING_12M_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description:
      "Annual churn rate based on rolling 12 months (churnRolling12MRate) as a single KPI metric (from AssetBreakdownAt). Accepts optional typeGroups filter to average matching segments.",
  },

  // Asset Historical IRR KPI - from AssetIrr query (property value + cash flows)
  [DataSource.ASSET_IRR_KPI]: {
    source: DataSource.ASSET_IRR_KPI,
    dataType: "key-value",
    queryGroup: "asset.irr",
    description:
      "Historical IRR (Internal Rate of Return) from acquisition date to selected period. Uses property value as GAV and income statement cash flows from a configurable categoryType.",
    tooltipKey: "CANVAS_DS_TOOLTIP_ASSET_IRR_KPI",
  },

  [DataSource.ASSET_TOP_TENANCIES_BY_RENT]: {
    source: DataSource.ASSET_TOP_TENANCIES_BY_RENT,
    dataType: "table",
    queryGroup: "asset.topTenancies",
    description: "Top 5 tenancies by yearly rent. Columns: name, type, area (sqm), yearly rent, status.",
  },
  [DataSource.ASSET_RENTED_UNITS_KPI]: {
    source: DataSource.ASSET_RENTED_UNITS_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Number of rented (occupied) units. Accepts optional typeGroups filter.",
  },
  [DataSource.ASSET_VACANT_UNITS_KPI]: {
    source: DataSource.ASSET_VACANT_UNITS_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Number of vacant units. Accepts optional typeGroups filter.",
  },
  [DataSource.ASSET_RENTED_AREA_KPI]: {
    source: DataSource.ASSET_RENTED_AREA_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Rented (occupied) area in square meters. Accepts optional typeGroups filter.",
  },
  [DataSource.ASSET_VACANT_AREA_KPI]: {
    source: DataSource.ASSET_VACANT_AREA_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Vacant area in square meters. Accepts optional typeGroups filter.",
  },
  [DataSource.ASSET_VACANCY_AMOUNT_KPI]: {
    source: DataSource.ASSET_VACANCY_AMOUNT_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdown",
    description: "Vacancy amount (DKK lost to vacancy annually) from AssetBreakdownAt. Uses BASE_RENT idleAnnualAmount. Accepts optional typeGroups filter.",
  },

  [DataSource.ASSET_RENTAL_INCOME_KPI]: {
    source: DataSource.ASSET_RENTAL_INCOME_KPI,
    dataType: "key-value",
    queryGroup: "asset.operatingMargin",
    description: "Rental income (GRI) for the latest period as a single KPI value from income statements.",
  },
  [DataSource.ASSET_NOI_MARGIN_KPI]: {
    source: DataSource.ASSET_NOI_MARGIN_KPI,
    dataType: "key-value",
    queryGroup: "asset.operatingMargin",
    description: "NOI margin (NOI / GRI × 100) for the latest period as a single KPI percentage.",
  },
  [DataSource.ASSET_RESULTS_MARGIN_KPI]: {
    source: DataSource.ASSET_RESULTS_MARGIN_KPI,
    dataType: "key-value",
    queryGroup: "asset.operatingMargin",
    description: "Results margin (full result / GRI × 100) for the latest period as a single KPI percentage.",
  },
  [DataSource.ASSET_INCOME_NOMINAL_OVER_TIME]: {
    source: DataSource.ASSET_INCOME_NOMINAL_OVER_TIME,
    dataType: "chart-timeseries",
    queryGroup: "asset.operatingMargin",
    description: "Combined nominal time series showing rental income (GRI), NOI, and full result as separate series.",
  },
  [DataSource.ASSET_INCOME_BY_CATEGORY]: {
    source: DataSource.ASSET_INCOME_BY_CATEGORY,
    dataType: "table",
    queryGroup: "asset.incomeByCategory",
    description:
      "Per-property table with one row per income category: Gross Rental Income, NOI, and Result. " +
      "Columns: category, realized, budget, realizedPrev. " +
      "Range is driven by canvas dateRange (or widget variables.dateRange). " +
      "Defaults: to = end of last fully completed month, from = Jan 1 of that year. " +
      "realizedPrev uses the same window offset by −1 year. 'Result' is the full P&L result — " +
      "the root category whose aggregationKind is Income, which is not exposed as a categoryType enum.",
  },
  [DataSource.ASSET_RENT_GROWTH_ROLLING_12M_KPI]: {
    source: DataSource.ASSET_RENT_GROWTH_ROLLING_12M_KPI,
    dataType: "key-value",
    queryGroup: "asset.breakdownBetween",
    description:
      "Rent growth rolling 12 months as a percentage. Compares the latest monthly BASE_RENT (Total) to the value 12 months earlier. " +
      "Requires the breakdownBetween date range to span at least 13 months.",
  },
  [DataSource.ASSET_TENANT_RENT_DISTRIBUTION]: {
    source: DataSource.ASSET_TENANT_RENT_DISTRIBUTION,
    dataType: "chart-simple",
    queryGroup: "asset.topTenancies",
    description:
      "Tenant rent distribution pie/donut chart. Rent is aggregated per tenant (a tenant's tenancies are summed). " +
      "Ghost/vacant tenants are excluded. Top 10 tenants shown individually by share of total annual contract rent (%), " +
      "remainder grouped as 'Øvrige'. Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER) — " +
      "tenancyType is mapped: RESIDENTIAL→RESIDENTIAL, OTHER/UNKNOWN/empty→OTHER, anything else→COMMERCIAL.",
  },
  [DataSource.ASSET_ARREARS_TOTAL_KPI]: {
    source: DataSource.ASSET_ARREARS_TOTAL_KPI,
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Total arrears (balance) for the property.",
  },
  [DataSource.ASSET_ARREARS_CURRENT_KPI]: {
    source: DataSource.ASSET_ARREARS_CURRENT_KPI,
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Current (not-yet-due) arrears for the property.",
  },
  [DataSource.ASSET_ARREARS_DUE_TOTAL_KPI]: {
    source: DataSource.ASSET_ARREARS_DUE_TOTAL_KPI,
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description:
      "Total overdue arrears for the property — the sum of the aged buckets " +
      "(0–30 + 31–60 + 61–90 + 90+ days), EXCLUDING the not-yet-due 'current' bucket.",
  },
  [DataSource.ASSET_ARREARS_DUE_0_30_KPI]: {
    source: DataSource.ASSET_ARREARS_DUE_0_30_KPI,
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 0–30 days for the property.",
  },
  [DataSource.ASSET_ARREARS_DUE_31_60_KPI]: {
    source: DataSource.ASSET_ARREARS_DUE_31_60_KPI,
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 31–60 days for the property.",
  },
  [DataSource.ASSET_ARREARS_DUE_61_90_KPI]: {
    source: DataSource.ASSET_ARREARS_DUE_61_90_KPI,
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 61–90 days for the property.",
  },
  [DataSource.ASSET_ARREARS_DUE_90_PLUS_KPI]: {
    source: DataSource.ASSET_ARREARS_DUE_90_PLUS_KPI,
    dataType: "key-value",
    queryGroup: "asset.assetAging",
    description: "Arrears due 90+ days for the property.",
  },
  [DataSource.ASSET_ARREARS_BY_LEASE]: {
    source: DataSource.ASSET_ARREARS_BY_LEASE,
    dataType: "table",
    queryGroup: "asset.arrears",
    description:
      "Top 10 leases by arrears (balance) descending. Columns: tenant, typeGroup, evictionStatus " +
      "(EVICTION | NONE), dueBand (oldest non-zero bucket: 90+ | 61-90 | 31-60 | 0-30 | CURRENT | NONE), amount. " +
      "Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER) and optional excludeDueBands " +
      'blocklist to drop rows by dueBand (e.g. ["CURRENT"]).',
  },
  [DataSource.ASSET_RENT_AT_LEASE_START_SCATTER]: {
    source: DataSource.ASSET_RENT_AT_LEASE_START_SCATTER,
    dataType: "chart-scatter",
    queryGroup: "asset.rentAtLeaseStart",
    description:
      "Scatter of rent at lease start: one point per lease, x = lease start date, y = gross annual rent at " +
      "that start, series = typeGroup. Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER).",
  },
  [DataSource.ASSET_VACANT_TENANCIES_TABLE]: {
    source: DataSource.ASSET_VACANT_TENANCIES_TABLE,
    dataType: "table",
    queryGroup: "asset.topTenancies",
    description:
      "Vacant tenancies table — one row per tenancy whose current tenant is a ghost (vacant placeholder). " +
      "Columns: identifier, typeGroup, area, annualRent (potential), status. Sorted by annualRent desc. " +
      "Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER).",
  },
  [DataSource.ASSET_BREAK_OPTIONS_TABLE]: {
    source: DataSource.ASSET_BREAK_OPTIONS_TABLE,
    dataType: "table",
    queryGroup: "asset.breakOptions",
    description:
      "Tenancies whose next break opportunity falls within a configurable horizon (default 12 months; " +
      "override via variables.horizonMonths). Which side counts is controlled by variables.breakSide: " +
      "'both' (default — earliest of landlord and tenant), 'landlord' (only irrevocableUntilLandlord), " +
      "or 'tenant' (only irrevocableUntilTenant). Sorted by next-break date ascending. " +
      "Columns: identifier, typeGroup, area, annualRent, status (Udlejer | Lejer | Begge), nextBreakDate. " +
      "Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER).",
  },
  [DataSource.ASSET_VACANCY_RATE_AREA_VS_ECONOMIC]: {
    source: DataSource.ASSET_VACANCY_RATE_AREA_VS_ECONOMIC,
    dataType: "chart-timeseries",
    queryGroup: "asset.breakdownBetween",
    description:
      "Area vs economic vacancy over time as two series on one chart. " +
      "Areal-tomgang % = idle area / total area. " +
      "Økonomisk tomgang % = idle BASE_RENT / (contract + idle BASE_RENT). " +
      "Accepts optional typeGroups filter (aggregates both metrics across matching segments).",
  },
  [DataSource.ASSET_NEW_LETTINGS_BY_TYPE_GROUP]: {
    source: DataSource.ASSET_NEW_LETTINGS_BY_TYPE_GROUP,
    dataType: "chart-simple",
    queryGroup: "asset.topTenancies",
    description:
      "New lettings in the last N months distributed by usage type (typeGroup) — donut/pie chart. " +
      "Counts tenancies whose current (non-ghost) tenant moved in within the configurable horizon " +
      "(default 12 months; override via variables.horizonMonths). " +
      "Accepts optional typeGroups filter (RESIDENTIAL | COMMERCIAL | OTHER).",
  },
  [DataSource.ASSET_MASTER_DATA]: {
    source: DataSource.ASSET_MASTER_DATA,
    dataType: "key-value" as const,
    queryGroup: "asset.masterData",
    description:
      "Property master data — name, Adm ID (externalID of type 'id'), and total number of units. " +
      "Returns a KeyValueListData object for display in the generic key-value list widget.",
  },
};

// ============================================================================
// SOURCE METADATA REGISTRY
// ============================================================================

/**
 * Single runtime registry for ALL canvas data source metadata.
 *
 * All sources — asset.*, financial.*, vacancy.*, etc. — are stored here.
 * Every source file calls registerSourceMetadata() at module-load time;
 * the providers barrel (composables/canvas/providers/index.ts) guarantees
 * all source files are imported before any data is fetched.
 *
 * This is the single source of truth for runtime metadata lookup and enumeration.
 */
const SOURCE_METADATA_REGISTRY = new Map<string, SourceMetadata>();

/**
 * Register source metadata for canvas data sources.
 *
 * Call this at module-load time from source definition files.
 * Works uniformly for built-in and org-specific sources alike.
 *
 * @example
 * ```typescript
 * // In helpers/canvas/sources/financial-sources.ts
 * registerSourceMetadata({
 *   "financial.incomeOverview": {
 *     dataType: "chart-timeseries",
 *     queryGroup: "reports.results",
 *   },
 * });
 * ```
 */
export function registerSourceMetadata(entries: Record<string, Omit<SourceMetadata, "source">>): void {
  for (const [key, meta] of Object.entries(entries)) {
    SOURCE_METADATA_REGISTRY.set(key, { source: key, ...meta });
  }
}

/**
 * Look up source metadata for any source key.
 *
 * Used by the data-source analyzer and provider routing layer.
 */
export function lookupSourceMetadata(source: string): SourceMetadata | undefined {
  return SOURCE_METADATA_REGISTRY.get(source);
}

/**
 * Return all registered source metadata entries.
 *
 * Used by canvas-reference.ts to enumerate all data sources (including
 * provider-specific ones) for the AI wizard and documentation.
 */
export function getAllSourceMetadata(): SourceMetadata[] {
  return Array.from(SOURCE_METADATA_REGISTRY.values());
}

// ============================================================================
// TYPE-SAFE DATA SOURCE HELPERS
// ============================================================================

/**
 * Data type categories
 */
export type DataType = "key-value" | "chart-simple" | "chart-multi" | "chart-timeseries" | "chart-scatter" | "chart-sunburst" | "table" | "text";

/**
 * Extract data sources by their data type.
 * This creates a union type of all DataSource enum values that match a specific data type.
 */
export type DataSourcesByType<T extends DataType> = {
  [K in DataSource]: (typeof SOURCE_METADATA)[K]["dataType"] extends T ? K : never;
}[DataSource];

/**
 * Data sources that return key-value data (single metric with optional change indicator).
 * Compatible with: key-value widgets
 */
export type KeyValueDataSource = DataSourcesByType<"key-value">;

/**
 * Data sources that return simple chart data (single series, pie/donut charts).
 * Compatible with: pie charts, donut charts, simple bar charts
 */
export type SimpleChartDataSource = DataSourcesByType<"chart-simple">;

/**
 * Data sources that return time series data (multiple series over time).
 * Compatible with: line charts, area charts, bar charts with time axis
 */
export type TimeSeriesDataSource = DataSourcesByType<"chart-timeseries">;

/**
 * Data sources that return multi-series chart data.
 * Compatible with: complex charts with multiple data series
 */
export type MultiSeriesDataSource = DataSourcesByType<"chart-multi">;

/**
 * Data sources that return sunburst chart data (hierarchical two-level structure).
 * Compatible with: sunburst charts only (chartType: "sunburst")
 */
export type SunburstChartDataSource = DataSourcesByType<"chart-sunburst">;

/**
 * Data sources that return tabular data.
 * Compatible with: table widgets
 */
export type TableDataSource = DataSourcesByType<"table">;

/**
 * Data sources that return rich text data (HTML content).
 * Compatible with: text widgets
 */
export type TextDataSource = DataSourcesByType<"text">;

/**
 * Helper to get compatible data sources for a widget type.
 * This can be used in documentation and editor hints.
 */
export const COMPATIBLE_SOURCES = {
  "key-value": Object.entries(SOURCE_METADATA)
    .filter(([, meta]) => meta.dataType === "key-value")
    .map(([source]) => source as DataSource),

  "chart-simple": Object.entries(SOURCE_METADATA)
    .filter(([, meta]) => meta.dataType === "chart-simple")
    .map(([source]) => source as DataSource),

  "chart-timeseries": Object.entries(SOURCE_METADATA)
    .filter(([, meta]) => meta.dataType === "chart-timeseries")
    .map(([source]) => source as DataSource),

  "chart-multi": Object.entries(SOURCE_METADATA)
    .filter(([, meta]) => meta.dataType === "chart-multi")
    .map(([source]) => source as DataSource),

  "chart-sunburst": Object.entries(SOURCE_METADATA)
    .filter(([, meta]) => meta.dataType === "chart-sunburst")
    .map(([source]) => source as DataSource),

  table: Object.entries(SOURCE_METADATA)
    .filter(([, meta]) => meta.dataType === "table")
    .map(([source]) => source as DataSource),

  text: Object.entries(SOURCE_METADATA)
    .filter(([, meta]) => meta.dataType === "text")
    .map(([source]) => source as DataSource),
} as const;

/**
 * Validate that a data source is compatible with a widget type at runtime.
 *
 * @param source - The data source to check
 * @param requiredType - The required data type
 * @returns true if compatible, false otherwise
 */
export function isSourceCompatible(source: DataSource, requiredType: DataType): boolean {
  const metadata = SOURCE_METADATA[source];
  if (!metadata) return false;

  return metadata.dataType === requiredType;
}

/**
 * Get the data type for a given source.
 *
 * @param source - The data source
 * @returns The data type, or undefined if source not found
 */
export function getSourceDataType(source: DataSource): DataType | undefined {
  return SOURCE_METADATA[source]?.dataType;
}
